import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Building2, TrendingUp, CreditCard, Search,
  Shield, ShieldOff, ChevronDown, Loader2, ArrowUpDown,
  Activity, BarChart3, Layers, Trash2, Ticket, Plus, Power,
  Copy, CalendarDays, Star, Clock, Zap, Eye, Database,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { platformAdminAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const TIER_COLORS = {
  free: 'bg-zinc-100 text-zinc-600',
  starter: 'bg-zinc-100 text-zinc-700',
  pro: 'bg-blue-50 text-blue-700',
  business: 'bg-purple-50 text-purple-700',
  enterprise: 'bg-amber-50 text-amber-800',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'organizations', label: 'Organizations', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'admins', label: 'Admins', icon: Shield },
  { id: 'coupons', label: 'Coupons', icon: Ticket },
  { id: 'data', label: 'Data Management', icon: Database },
];

function MetricCard({ label, value, icon: Icon, accent }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500 font-medium">{label}</p>
            <p className="text-2xl font-bold text-zinc-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {value}
            </p>
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent || 'bg-zinc-100'}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OverviewTab({ metrics }) {
  if (!metrics) return <div className="text-center py-12 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const tierDist = metrics.tier_distribution || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Organizations" value={metrics.total_orgs} icon={Building2} accent="bg-[#D94E18]/10 text-[#D94E18]" />
        <MetricCard label="Total Users" value={metrics.total_users} icon={Users} accent="bg-blue-50 text-blue-600" />
        <MetricCard label="Signups (30d)" value={metrics.recent_signups_30d} icon={TrendingUp} accent="bg-emerald-50 text-emerald-600" />
        <MetricCard label="Payments" value={metrics.total_payments} icon={CreditCard} accent="bg-purple-50 text-purple-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Tier Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['free', 'starter', 'pro', 'business', 'enterprise'].map(tier => {
                const count = tierDist[tier] || 0;
                const pct = metrics.total_orgs > 0 ? (count / metrics.total_orgs * 100) : 0;
                return (
                  <div key={tier} className="flex items-center gap-3">
                    <Badge className={`${TIER_COLORS[tier]} text-xs w-24 justify-center`}>{tier}</Badge>
                    <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#D94E18] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-sm font-mono text-zinc-600 w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Platform Credit Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-zinc-500">Monthly Used (all orgs)</span>
                <span className="text-lg font-semibold font-mono text-zinc-900">{metrics.credit_stats?.total_monthly_used || 0}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-zinc-500">Purchased Balance (all orgs)</span>
                <span className="text-lg font-semibold font-mono text-zinc-900">{metrics.credit_stats?.total_purchased || 0}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-zinc-500">Total Transactions Processed</span>
                <span className="text-lg font-semibold font-mono text-zinc-900">{metrics.total_transactions}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-zinc-500">Total Journal Entries</span>
                <span className="text-lg font-semibold font-mono text-zinc-900">{metrics.total_journal_entries}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OrganizationsTab() {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [actionValue, setActionValue] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (tierFilter) params.tier = tierFilter;
      const res = await platformAdminAPI.getOrganizations(params);
      setOrgs(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load organizations');
    }
    setLoading(false);
  }, [search, tierFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      if (actionDialog.type === 'credits') {
        await platformAdminAPI.adjustCredits(actionDialog.orgId, parseInt(actionValue) || 0, actionReason);
        toast.success('Credits adjusted');
      } else if (actionDialog.type === 'tier') {
        await platformAdminAPI.changeTier(actionDialog.orgId, actionValue);
        toast.success('Tier changed');
      } else if (actionDialog.type === 'cancel_sub') {
        await platformAdminAPI.cancelSubscription(actionDialog.orgId);
        toast.success('Subscription cancelled');
      }
      setActionDialog(null);
      setActionValue('');
      setActionReason('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setProcessing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by name or ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="org-search"
          />
        </div>
        <select
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 px-3 text-sm"
          data-testid="org-tier-filter"
        >
          <option value="">All tiers</option>
          <option value="free">Free</option>
          <option value="starter">Starter</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <Badge variant="outline" className="text-xs">{total} total</Badge>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="orgs-table">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Organization</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Tier</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Members</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Credits Used</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Purchased</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" /></td></tr>
              ) : orgs.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-zinc-400">No organizations found</td></tr>
              ) : orgs.map(org => (
                <tr key={org.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{org.company_name || 'Unnamed'}</p>
                    <p className="text-xs text-zinc-400 font-mono">{org.id.substring(0, 8)}...</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`${TIER_COLORS[org.tier] || TIER_COLORS.free} text-xs`}>{org.tier || 'free'}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono">{org.member_count}</td>
                  <td className="px-4 py-3 font-mono">{org.credits_summary?.monthly_used || 0}/{org.credits_summary?.monthly_allocation || 0}</td>
                  <td className="px-4 py-3 font-mono">{org.credits_summary?.purchased_balance || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => { setActionDialog({ type: 'credits', orgId: org.id, name: org.company_name || org.id }); setActionValue(''); }}
                        data-testid={`adjust-credits-${org.id.substring(0,8)}`}
                      >
                        Credits
                      </Button>
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => { setActionDialog({ type: 'tier', orgId: org.id, name: org.company_name || org.id, currentTier: org.tier }); setActionValue(org.tier || 'free'); }}
                        data-testid={`change-tier-${org.id.substring(0,8)}`}
                      >
                        Tier
                      </Button>
                      {org.tier !== 'free' && (
                        <Button
                          variant="outline" size="sm" className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => { setActionDialog({ type: 'cancel_sub', orgId: org.id, name: org.company_name || org.id }); }}
                          data-testid={`cancel-sub-${org.id.substring(0,8)}`}
                        >
                          Cancel Sub
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">
              {actionDialog?.type === 'credits' ? 'Adjust Credits' : actionDialog?.type === 'cancel_sub' ? 'Cancel Subscription' : 'Change Tier'}
            </DialogTitle>
            <DialogDescription className="text-xs">{actionDialog?.name}</DialogDescription>
          </DialogHeader>
          {actionDialog?.type === 'cancel_sub' ? (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600">
                This will cancel the Stripe subscription and revert <strong>{actionDialog?.name}</strong> to the free tier. This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2 pt-3">
                <Button variant="outline" size="sm" onClick={() => setActionDialog(null)}>Keep Subscription</Button>
                <Button size="sm" variant="destructive" onClick={handleAction} disabled={processing} data-testid="confirm-cancel-sub">
                  {processing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                  Cancel Subscription
                </Button>
              </div>
            </div>
          ) : actionDialog?.type === 'credits' ? (
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Amount (positive to add, negative to subtract)</Label>
                <Input type="number" value={actionValue} onChange={e => setActionValue(e.target.value)} placeholder="e.g. 100 or -50" data-testid="credit-amount-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Reason</Label>
                <Input value={actionReason} onChange={e => setActionReason(e.target.value)} placeholder="e.g. Promo credit" data-testid="credit-reason-input" />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 pt-2">
              <Label className="text-xs">New Tier</Label>
              <select
                value={actionValue}
                onChange={e => setActionValue(e.target.value)}
                className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm"
                data-testid="tier-select"
              >
                {['free', 'starter', 'pro', 'business', 'enterprise'].map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" size="sm" onClick={() => setActionDialog(null)}>Cancel</Button>
            <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white" onClick={handleAction} disabled={processing} data-testid="confirm-action-btn">
              {processing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await platformAdminAPI.getUsers(params);
      setUsers(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load users');
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSuspend = async (userId, email, currentlySuspended) => {
    setProcessing(userId);
    try {
      await platformAdminAPI.suspendUser(userId, !currentlySuspended);
      toast.success(`${email} ${currentlySuspended ? 'unsuspended' : 'suspended'}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setProcessing(null);
  };

  const handleToggleAdmin = async (userId, email, currentlyAdmin) => {
    setProcessing(userId);
    try {
      await platformAdminAPI.togglePlatformAdmin(userId, !currentlyAdmin);
      toast.success(`Admin access ${currentlyAdmin ? 'revoked for' : 'granted to'} ${email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
    setProcessing(null);
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setProcessing(userId);
    try {
      await platformAdminAPI.deleteUser(userId);
      toast.success(`${email} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
    setProcessing(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="user-search"
          />
        </div>
        <Badge variant="outline" className="text-xs">{total} total</Badge>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">User</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Role</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Org ID</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Status</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-8 text-zinc-400">No users found</td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-zinc-900">{u.name}</p>
                    <p className="text-xs text-zinc-400">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-xs">{u.role || 'viewer'}</Badge>
                      {u.is_platform_admin && (
                        <Badge className="bg-[#D94E18]/10 text-[#D94E18] text-[10px]">Platform Admin</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-400">{(u.org_id || '').substring(0, 8)}...</td>
                  <td className="px-4 py-3">
                    {u.suspended ? (
                      <Badge className="bg-red-50 text-red-600 text-xs">Suspended</Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-600 text-xs">Active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline" size="sm" className={`h-7 text-xs ${u.suspended ? 'border-emerald-300 text-emerald-700' : 'border-red-300 text-red-700'}`}
                        onClick={() => handleSuspend(u.id, u.email, u.suspended)}
                        disabled={processing === u.id || u.is_platform_admin}
                        data-testid={`suspend-${u.id.substring(0,8)}`}
                      >
                        {processing === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : u.suspended ? 'Unsuspend' : 'Suspend'}
                      </Button>
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => handleToggleAdmin(u.id, u.email, u.is_platform_admin)}
                        disabled={processing === u.id}
                        data-testid={`toggle-admin-${u.id.substring(0,8)}`}
                      >
                        {u.is_platform_admin ? <ShieldOff className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                      </Button>
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(u.id, u.email)}
                        disabled={processing === u.id || u.is_platform_admin || u.role === 'owner'}
                        data-testid={`delete-user-${u.id.substring(0,8)}`}
                        title={u.is_platform_admin ? 'Revoke admin first' : u.role === 'owner' ? 'Transfer ownership first' : 'Delete user'}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


const COUPON_TYPES = [
  { value: 'bonus_credits', label: 'Bonus Credits', desc: 'Add free credits to the org' },
  { value: 'discount_percent', label: '% Discount', desc: 'Discount on next subscription' },
  { value: 'free_tier_upgrade', label: 'Tier Upgrade', desc: 'Upgrade to a specific tier' },
];

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [form, setForm] = useState({
    code: '', type: 'bonus_credits', value: '', max_uses: '',
    expires_at: '', applicable_tiers: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await platformAdminAPI.getCoupons();
      setCoupons(res.data.items);
    } catch { toast.error('Failed to load coupons'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setProcessing('create');
    try {
      const payload = {
        code: form.code,
        type: form.type,
        value: form.type === 'free_tier_upgrade' ? form.value : parseFloat(form.value) || 0,
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null,
        applicable_tiers: form.applicable_tiers,
      };
      await platformAdminAPI.createCoupon(payload);
      toast.success(`Coupon "${form.code.toUpperCase()}" created`);
      setShowCreate(false);
      setForm({ code: '', type: 'bonus_credits', value: '', max_uses: '', expires_at: '', applicable_tiers: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create coupon');
    }
    setProcessing(null);
  };

  const handleToggle = async (coupon) => {
    setProcessing(coupon.id);
    try {
      await platformAdminAPI.toggleCoupon(coupon.id);
      toast.success(`Coupon "${coupon.code}" ${coupon.active ? 'deactivated' : 'activated'}`);
      load();
    } catch (err) { toast.error('Action failed'); }
    setProcessing(null);
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}" permanently?`)) return;
    setProcessing(coupon.id);
    try {
      await platformAdminAPI.deleteCoupon(coupon.id);
      toast.success(`Coupon "${coupon.code}" deleted`);
      load();
    } catch (err) { toast.error('Delete failed'); }
    setProcessing(null);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied');
  };

  const toggleTier = (tier) => {
    setForm(f => ({
      ...f,
      applicable_tiers: f.applicable_tiers.includes(tier)
        ? f.applicable_tiers.filter(t => t !== tier)
        : [...f.applicable_tiers, tier]
    }));
  };

  const typeLabel = (t) => COUPON_TYPES.find(c => c.value === t)?.label || t;

  const valueDisplay = (coupon) => {
    if (coupon.type === 'bonus_credits') return `+${coupon.value} credits`;
    if (coupon.type === 'discount_percent') return `${coupon.value}% off`;
    if (coupon.type === 'free_tier_upgrade') return `Upgrade to ${coupon.value}`;
    return coupon.value;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="text-xs">{coupons.length} coupons</Badge>
        <Button
          size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
          onClick={() => setShowCreate(true)}
          data-testid="create-coupon-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Create Coupon
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead>
              <tr className="border-b border-zinc-100 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Code</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Type</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Value</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Uses</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Expires</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Status</th>
                <th className="px-4 py-3 font-medium text-zinc-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" /></td></tr>
              ) : coupons.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-zinc-400">No coupons yet</td></tr>
              ) : coupons.map(c => (
                <tr key={c.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-semibold bg-zinc-100 px-2 py-1 rounded">{c.code}</code>
                      <button onClick={() => copyCode(c.code)} className="text-zinc-400 hover:text-zinc-600">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{typeLabel(c.type)}</td>
                  <td className="px-4 py-3 text-xs font-medium">{valueDisplay(c)}</td>
                  <td className="px-4 py-3 text-xs font-mono">{c.current_uses}/{c.max_uses || '∞'}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={c.active ? 'bg-emerald-50 text-emerald-600 text-xs' : 'bg-zinc-100 text-zinc-500 text-xs'}>
                      {c.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => handleToggle(c)}
                        disabled={processing === c.id}
                        data-testid={`toggle-coupon-${c.code}`}
                      >
                        <Power className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="outline" size="sm" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(c)}
                        disabled={processing === c.id}
                        data-testid={`delete-coupon-${c.code}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Coupon Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Create Coupon</DialogTitle>
            <DialogDescription className="text-xs">Create a promotional coupon for users to redeem</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Coupon Code</Label>
              <Input
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                placeholder="e.g. LAUNCH50"
                className="uppercase"
                data-testid="coupon-code-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value, value: '' }))}
                className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm"
                data-testid="coupon-type-select"
              >
                {COUPON_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">
                {form.type === 'bonus_credits' ? 'Credits Amount' : form.type === 'discount_percent' ? 'Discount %' : 'Target Tier'}
              </Label>
              {form.type === 'free_tier_upgrade' ? (
                <select
                  value={form.value}
                  onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                  className="w-full h-9 rounded-md border border-zinc-200 px-3 text-sm"
                  data-testid="coupon-value-input"
                >
                  <option value="">Select tier...</option>
                  {['pro', 'business', 'enterprise'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              ) : (
                <Input
                  type="number"
                  value={form.value}
                  onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
                  placeholder={form.type === 'bonus_credits' ? 'e.g. 100' : 'e.g. 20'}
                  data-testid="coupon-value-input"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Max Uses (0 = unlimited)</Label>
                <Input
                  type="number"
                  value={form.max_uses}
                  onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                  placeholder="0"
                  data-testid="coupon-max-uses-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Expires At</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  data-testid="coupon-expires-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Applicable Tiers (empty = all)</Label>
              <div className="flex gap-2 flex-wrap">
                {['starter', 'pro', 'business', 'enterprise'].map(tier => (
                  <button
                    key={tier}
                    type="button"
                    onClick={() => toggleTier(tier)}
                    className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                      form.applicable_tiers.includes(tier)
                        ? 'bg-[#D94E18] text-white border-[#D94E18]'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    {tier}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
              onClick={handleCreate}
              disabled={processing === 'create' || !form.code || !form.value}
              data-testid="submit-coupon-btn"
            >
              {processing === 'create' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AnalyticsTab() {
  const [overview, setOverview] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [npsData, setNpsData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ef, np] = await Promise.all([
        platformAdminAPI.getAnalyticsOverview(days),
        platformAdminAPI.getAnalyticsEfficiency(days),
        platformAdminAPI.getAnalyticsNps(days),
      ]);
      setOverview(ov.data);
      setEfficiency(ef.data);
      setNpsData(np.data);
    } catch {
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;

  const FEATURE_LABELS = {
    dashboard: 'Dashboard', upload: 'Upload', review: 'Review', journal: 'Journal',
    accounts: 'Accounts', invoices: 'Invoices', customers: 'Customers', reports: 'Reports',
    settings: 'Settings', chatbot: 'Chatbot', admin: 'Admin',
  };

  const maxFeatureCount = Math.max(...(overview?.feature_usage || []).map(f => f.count), 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Period selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-500">Period:</span>
        {[7, 30, 90].map(d => (
          <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
            onClick={() => setDays(d)} className={days === d ? 'bg-[#D94E18] hover:bg-[#c44515]' : ''}>
            {d}d
          </Button>
        ))}
      </div>

      {/* Active Users */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Daily Active Users" value={overview?.active_users?.daily || 0} icon={Users} />
        <MetricCard label="Weekly Active Users" value={overview?.active_users?.weekly || 0} icon={TrendingUp} />
        <MetricCard label="Monthly Active Users" value={overview?.active_users?.monthly || 0} icon={Activity} />
      </div>

      {/* User Classification + NPS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">User Classification</CardTitle>
            <CardDescription>Based on feature usage in the period</CardDescription>
          </CardHeader>
          <CardContent>
            {overview?.user_classification ? (
              <div className="space-y-3">
                {[
                  { label: 'Heavy users', value: overview.user_classification.heavy, color: 'bg-red-500' },
                  { label: 'Medium users', value: overview.user_classification.medium, color: 'bg-amber-500' },
                  { label: 'Light users', value: overview.user_classification.light, color: 'bg-emerald-500' },
                ].map(({ label, value, color }) => {
                  const total = (overview.user_classification.heavy + overview.user_classification.medium + overview.user_classification.light) || 1;
                  return (
                    <div key={label} className="flex items-center gap-3">
                      <span className="text-sm text-zinc-600 w-28">{label}</span>
                      <div className="flex-1 h-6 bg-zinc-100 rounded-full overflow-hidden">
                        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${(value / total) * 100}%` }} />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">{value}</span>
                    </div>
                  );
                })}
                <p className="text-xs text-zinc-400 mt-1">Light: 1-20 events · Medium: 21-100 · Heavy: 100+</p>
              </div>
            ) : <p className="text-sm text-zinc-400">No data yet</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" /> NPS Score
            </CardTitle>
            <CardDescription>{overview?.nps?.total_responses || 0} responses in {days}d</CardDescription>
          </CardHeader>
          <CardContent>
            {overview?.nps?.total_responses > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-zinc-800">{overview.nps.avg_rating}</span>
                  <span className="text-sm text-zinc-500">/ 5.0</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`w-5 h-5 ${s <= Math.round(overview.nps.avg_rating) ? 'fill-amber-400 text-amber-400' : 'text-zinc-200'}`} />
                    ))}
                  </div>
                </div>
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map(s => (
                    <div key={s} className="flex items-center gap-2 text-sm">
                      <span className="w-3 text-zinc-500">{s}</span>
                      <Star className="w-3 h-3 text-amber-400" />
                      <div className="flex-1 h-3 bg-zinc-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{
                          width: `${((overview.nps.distribution?.[String(s)] || 0) / overview.nps.total_responses) * 100}%`
                        }} />
                      </div>
                      <span className="w-6 text-right text-zinc-500">{overview.nps.distribution?.[String(s)] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-zinc-400">No NPS responses yet. Users are prompted after 7 days of usage.</p>}
          </CardContent>
        </Card>
      </div>

      {/* Feature Usage Heat Map */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4" /> Feature Usage
          </CardTitle>
          <CardDescription>Which features users interact with most</CardDescription>
        </CardHeader>
        <CardContent>
          {overview?.feature_usage?.length > 0 ? (
            <div className="space-y-2">
              {overview.feature_usage.map((f) => {
                const pct = (f.count / maxFeatureCount) * 100;
                const intensity = pct > 66 ? 'bg-red-500' : pct > 33 ? 'bg-amber-500' : 'bg-emerald-500';
                return (
                  <div key={f.feature} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-600 w-24 truncate">{FEATURE_LABELS[f.feature] || f.feature}</span>
                    <div className="flex-1 h-7 bg-zinc-50 rounded overflow-hidden relative">
                      <div className={`h-full ${intensity} rounded transition-all opacity-80`} style={{ width: `${pct}%` }} />
                      <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-zinc-700">
                        {f.count} events · {f.unique_users} users
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-zinc-400">No feature usage data yet. Events are tracked as users navigate the app.</p>}
        </CardContent>
      </Card>

      {/* Operational Efficiency */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" /> Operational Efficiency
          </CardTitle>
          <CardDescription>Average time users spend on each operation</CardDescription>
        </CardHeader>
        <CardContent>
          {efficiency?.metrics?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="text-left py-2 font-medium text-zinc-500">Feature</th>
                    <th className="text-left py-2 font-medium text-zinc-500">Action</th>
                    <th className="text-right py-2 font-medium text-zinc-500">Count</th>
                    <th className="text-right py-2 font-medium text-zinc-500">Avg</th>
                    <th className="text-right py-2 font-medium text-zinc-500">P50</th>
                    <th className="text-right py-2 font-medium text-zinc-500">P90</th>
                    <th className="text-right py-2 font-medium text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {efficiency.metrics.map((m) => {
                    const avgSec = (m.avg_ms / 1000).toFixed(1);
                    const status = m.avg_ms > 60000 ? 'slow' : m.avg_ms > 15000 ? 'moderate' : 'fast';
                    return (
                      <tr key={`${m.feature}-${m.action}`} className="border-b border-zinc-50">
                        <td className="py-2 text-zinc-700">{FEATURE_LABELS[m.feature] || m.feature}</td>
                        <td className="py-2 text-zinc-500">{m.action}</td>
                        <td className="py-2 text-right text-zinc-600">{m.count}</td>
                        <td className="py-2 text-right font-medium">{avgSec}s</td>
                        <td className="py-2 text-right text-zinc-500">{(m.p50_ms / 1000).toFixed(1)}s</td>
                        <td className="py-2 text-right text-zinc-500">{(m.p90_ms / 1000).toFixed(1)}s</td>
                        <td className="py-2 text-right">
                          <Badge variant="outline" className={
                            status === 'slow' ? 'border-red-200 text-red-600 bg-red-50' :
                            status === 'moderate' ? 'border-amber-200 text-amber-600 bg-amber-50' :
                            'border-emerald-200 text-emerald-600 bg-emerald-50'
                          }>
                            {status === 'slow' ? 'Slow' : status === 'moderate' ? 'Moderate' : 'Fast'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-zinc-400">No efficiency data yet. Durations are tracked when users complete operations.</p>}
        </CardContent>
      </Card>
    </div>
  );
}


function AdminsTab() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await platformAdminAPI.listAdmins();
      setAdmins(res.data.admins || []);
    } catch {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  const handleGrant = async () => {
    if (!email.trim()) return;
    setGranting(true);
    try {
      const res = await platformAdminAPI.grantAdminByEmail(email.trim());
      toast.success(res.data.message);
      setEmail('');
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant admin access');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (userId, adminEmail) => {
    if (!window.confirm(`Revoke admin access for ${adminEmail}?`)) return;
    setRevoking(userId);
    try {
      await platformAdminAPI.revokeAdmin(userId);
      toast.success(`Admin access revoked for ${adminEmail}`);
      loadAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke admin');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Grant Admin by Email */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#D94E18]" />Add Admin
          </CardTitle>
          <CardDescription>Enter the email of a registered user to grant them admin access.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3" data-testid="grant-admin-section">
            <Input
              placeholder="user@company.com"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="max-w-sm"
              onKeyDown={e => e.key === 'Enter' && handleGrant()}
              data-testid="admin-email-input"
            />
            <Button
              onClick={handleGrant}
              disabled={granting || !email.trim()}
              className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
              data-testid="grant-admin-btn"
            >
              {granting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Shield className="w-4 h-4 mr-1.5" />}
              Grant Admin
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Admins List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Current Admins ({admins.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" />Loading...
            </div>
          ) : admins.length === 0 ? (
            <p className="text-sm text-zinc-400 py-4">No admins configured yet.</p>
          ) : (
            <div className="divide-y divide-zinc-100">
              {admins.map(admin => (
                <div key={admin.id} className="flex items-center justify-between py-3" data-testid={`admin-row-${admin.id.substring(0,8)}`}>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{admin.name || 'Unnamed'}</p>
                    <p className="text-xs text-zinc-500">{admin.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#D94E18]/10 text-[#D94E18] border-[#D94E18]/20">Admin</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleRevoke(admin.id, admin.email)}
                      disabled={revoking === admin.id}
                      data-testid={`revoke-admin-${admin.id.substring(0,8)}`}
                    >
                      {revoking === admin.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <ShieldOff className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}




function DataManagementTab() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await platformAdminAPI.getDataStats();
      setStats(res.data);
    } catch {
      toast.error('Failed to load data stats');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePurge = async (action) => {
    if (confirmText !== 'DELETE') return;
    setProcessing(true);
    try {
      let res;
      const orgId = user?.org_id;
      if (action === 'organizations') {
        res = await platformAdminAPI.purgeOrganizations(orgId);
      } else if (action === 'transactions') {
        res = await platformAdminAPI.purgeTransactions();
      } else if (action === 'analytics') {
        res = await platformAdminAPI.purgeAnalytics();
      } else if (action === 'full-reset') {
        res = await platformAdminAPI.fullPlatformReset(orgId);
      }
      setResult(res.data);
      toast.success(res.data?.message || 'Data purged');
      setActiveAction(null);
      setConfirmText('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    }
    setProcessing(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;

  const CATEGORIES = [
    {
      id: 'organizations',
      label: 'Test Organizations & Users',
      description: 'Delete all organizations and their users except your own (Inopsia AI). Invitations are also removed.',
      icon: Building2,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
      collections: [
        { name: 'Organizations', count: stats?.organization || 0 },
        { name: 'Users', count: stats?.users || 0 },
        { name: 'Invitations', count: stats?.invitations || 0 },
      ],
    },
    {
      id: 'transactions',
      label: 'Transactional & GL Data',
      description: 'Delete all transactions, journal entries, invoices, customers, accounts, and recurring entries across all organizations.',
      icon: CreditCard,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      collections: [
        { name: 'Transactions', count: stats?.transactions || 0 },
        { name: 'Journal Entries', count: stats?.journal_entries || 0 },
        { name: 'Invoices', count: stats?.invoices || 0 },
        { name: 'Customers', count: stats?.customers || 0 },
        { name: 'Accounts (CoA)', count: stats?.accounts || 0 },
        { name: 'Recurring Entries', count: stats?.recurring_entries || 0 },
        { name: 'Merchant Mappings', count: stats?.merchant_mappings || 0 },
      ],
    },
    {
      id: 'analytics',
      label: 'Analytics & Admin Data',
      description: 'Delete all analytics events, NPS responses, notifications, payment records, and audit logs.',
      icon: BarChart3,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      collections: [
        { name: 'Analytics Events', count: stats?.analytics_events || 0 },
        { name: 'NPS Responses', count: stats?.nps_responses || 0 },
        { name: 'Notifications', count: stats?.notifications || 0 },
        { name: 'Payment Transactions', count: stats?.payment_transactions || 0 },
        { name: 'Audit Log', count: stats?.admin_audit_log || 0 },
      ],
    },
  ];

  const totalRecords = Object.values(stats || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="data-management-tab">
      {/* Summary */}
      <Card className="border-zinc-200">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-500">Total records across all collections</p>
              <p className="text-2xl font-bold text-zinc-800">{totalRecords.toLocaleString()}</p>
            </div>
            <Button size="sm" variant="outline" onClick={load} data-testid="refresh-stats">
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Category Cards */}
      {CATEGORIES.map(cat => {
        const Icon = cat.icon;
        const catTotal = cat.collections.reduce((a, c) => a + c.count, 0);
        return (
          <Card key={cat.id} className="border-zinc-200">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg border ${cat.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{cat.label}</CardTitle>
                    <CardDescription className="text-xs">{cat.description}</CardDescription>
                  </div>
                </div>
                <Button
                  size="sm" variant="destructive"
                  onClick={() => { setActiveAction(cat.id); setConfirmText(''); setResult(null); }}
                  disabled={catTotal === 0}
                  data-testid={`purge-${cat.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Purge
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cat.collections.map(c => (
                  <div key={c.name} className="bg-zinc-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-zinc-500">{c.name}</p>
                    <p className="text-lg font-semibold text-zinc-700">{c.count.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Full Platform Reset */}
      <Card className="border-red-200 bg-red-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg border border-red-200 bg-red-50 text-red-600">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-base text-red-800">Full Platform Reset</CardTitle>
                <CardDescription className="text-xs text-red-600">
                  Delete ALL data except your organization and admin account. This cannot be undone.
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm" variant="destructive"
              onClick={() => { setActiveAction('full-reset'); setConfirmText(''); setResult(null); }}
              data-testid="purge-full-reset"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Reset Everything
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Last Result */}
      {result && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="pt-5">
            <p className="text-sm font-medium text-emerald-800">{result.message}</p>
            {result.details && (
              <div className="mt-2 grid grid-cols-3 md:grid-cols-5 gap-2">
                {Object.entries(result.details).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <span className="text-zinc-500">{k}:</span> <span className="font-medium">{v}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!activeAction} onOpenChange={() => setActiveAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {activeAction === 'full-reset' ? 'Full Platform Reset' : `Purge ${activeAction}`}
            </DialogTitle>
            <DialogDescription>
              This action is <strong>permanent and cannot be undone</strong>.
              {activeAction === 'full-reset' && ' All data will be deleted except your organization and admin account.'}
              {activeAction === 'organizations' && ' All test organizations and their users will be deleted. Your org (Inopsia AI) is preserved.'}
              {activeAction === 'transactions' && ' All transactions, journal entries, invoices, customers, and accounts will be deleted from ALL organizations.'}
              {activeAction === 'analytics' && ' All analytics events, NPS responses, notifications, and audit logs will be deleted.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-zinc-600 block mb-1">Type <strong>DELETE</strong> to confirm:</label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
                data-testid="confirm-delete-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setActiveAction(null)}>Cancel</Button>
              <Button
                size="sm" variant="destructive"
                disabled={confirmText !== 'DELETE' || processing}
                onClick={() => handlePurge(activeAction)}
                data-testid="confirm-purge-btn"
              >
                {processing && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                {activeAction === 'full-reset' ? 'Reset Everything' : 'Purge Data'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function PlatformAdminPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    platformAdminAPI.getDashboard()
      .then(res => setMetrics(res.data))
      .catch(() => toast.error('Failed to load dashboard metrics'));
  }, []);

  return (
    <div className="max-w-7xl mx-auto" data-testid="platform-admin-page">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Kaya Platform
        </h1>
        <p className="text-sm text-zinc-500 mt-0.5">Manage all organizations, users, and platform-wide settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#D94E18] text-[#D94E18]'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab metrics={metrics} />}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'organizations' && <OrganizationsTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'admins' && <AdminsTab />}
      {activeTab === 'coupons' && <CouponsTab />}
      {activeTab === 'data' && <DataManagementTab />}
    </div>
  );
}
